import { getSupabase } from '@/lib/supabase'

const SIGNED_URL_TIMEOUT_MS = 5000
const UNAVAILABLE_PHOTO_URL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='

type PhotoRow = Record<string, unknown>

function text(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value)
}

function photoKey(bucket: string, path: string): string {
  return `${bucket}\u0000${path}`
}

function withTimeout<T>(promise: PromiseLike<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('signed photo URL timeout')), timeoutMs)
    Promise.resolve(promise).then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

/**
 * Signs all photos in one request per bucket. A storage outage must not make
 * the already loaded review unavailable; failed photos simply get no preview.
 */
export async function resolveTrainerReviewPhotoUrls(rows: PhotoRow[]): Promise<Map<string, string>> {
  const grouped = new Map<string, Set<string>>()
  for (const row of rows) {
    const bucket = text(row.storage_bucket, 'trainer-review-photos')
    const path = text(row.storage_path)
    if (!path) continue
    const paths = grouped.get(bucket) ?? new Set<string>()
    paths.add(path)
    grouped.set(bucket, paths)
  }

  const resolved = new Map<string, string>()
  await Promise.all([...grouped.entries()].map(async ([bucket, pathSet]) => {
    try {
      const { data, error } = await withTimeout(
        getSupabase().storage.from(bucket).createSignedUrls([...pathSet], 60 * 60),
        SIGNED_URL_TIMEOUT_MS,
      )
      if (error || !data) return
      for (const item of data) {
        if (item.path && item.signedUrl) {
          resolved.set(photoKey(bucket, item.path), item.signedUrl)
        }
      }
    } catch {
      // The review itself is still usable without a problematic photo.
    }
  }))

  return resolved
}

export function getTrainerReviewPhotoUrl(
  row: PhotoRow,
  resolved: Map<string, string>,
): string {
  const bucket = text(row.storage_bucket, 'trainer-review-photos')
  const path = text(row.storage_path)
  return resolved.get(photoKey(bucket, path)) || text(row.external_url) || UNAVAILABLE_PHOTO_URL
}
